export interface Money {
  amount: string | number;
  currency: string;
}

export const isCurrencyCode = (currency: unknown): currency is string =>
  typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)

// Keep a 100-fold margin below the safe-integer cent limit so conversion to
// major units retains cents. Larger monetary values are unavailable, not rounded.
const MAX_MAJOR_AMOUNT = Math.floor(Number.MAX_SAFE_INTEGER / 10000)
const MAX_MINOR_AMOUNT = MAX_MAJOR_AMOUNT * 100

const decimalAmount = (amount: unknown): number | null => {
  if (typeof amount !== 'number' && (typeof amount !== 'string' || !/^-?\d+(\.\d+)?$/.test(amount))) {
    return null
  }
  const value = Number(amount)
  return Number.isFinite(value) && Math.abs(value) <= MAX_MAJOR_AMOUNT ? value : null
}

export const minorAmount = (money: Money | null | undefined): number | null => {
  if (!money || !isCurrencyCode(money.currency)) return null
  if (typeof money.amount !== 'number' && (typeof money.amount !== 'string' || !/^-?\d+$/.test(money.amount))) {
    return null
  }
  const amount = Number(money.amount)
  return Number.isSafeInteger(amount) && Math.abs(amount) <= MAX_MINOR_AMOUNT ? amount : null
}

export const formatMajorMoney = (amount: unknown, currency: unknown): string => {
  const value = decimalAmount(amount)
  if (value === null || !isCurrencyCode(currency)) return '—'
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
  }).format(value)
}

export const formatMoney = (money: Money | null | undefined): string => {
  const amount = minorAmount(money)
  return amount === null ? '—' : formatMajorMoney(amount / 100, money!.currency)
}

// Only supplier invoice summaries serialize major units with PHP number_format.
// Other endpoints must not silently accept grouped or localized numeric strings.
export const parseSupplierMajorAmount = (amount: unknown): number | null => {
  if (typeof amount === 'string' && /^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(amount)) {
    return decimalAmount(amount.replace(/,/g, ''))
  }
  return decimalAmount(amount)
}

export const subtractMoney = (total: Money | null | undefined, deductions: Array<Money | null | undefined>): Money | null => {
  let amount = minorAmount(total)
  if (amount === null || !total) return null
  for (const deduction of deductions) {
    const value = minorAmount(deduction)
    if (value === null || !deduction || deduction.currency !== total.currency) return null
    amount -= value
    if (!Number.isSafeInteger(amount) || Math.abs(amount) > MAX_MINOR_AMOUNT) return null
  }
  return { amount: String(amount), currency: total.currency }
}
